import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { Alert } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { Users } from "lucide-react";
import { TAP_TARGET, mediaPhone } from "@/shared/theme/breakpoints";
import { pressedTint } from "@/shared/theme/pressed";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
	CampaignPipelineStage,
	CampaignStatusTrackingCreator,
	CampaignStatusTrackingData,
} from "@/api/campaignStatusTracking";
import { useCampaignStatusTracking } from "../../hooks/useCampaignStatusTracking";
import {
	computeDistributionSegments,
	formatUpdatedAtValue,
	getVisibleStageCards,
} from "../../utils/campaignStatusTrackingUtils";
import {
	StatusTrackingViewContext,
	useCampaignStatusTrackingAnalytics,
} from "../../utils/campaignStatusTrackingAnalytics";
import { StatusDistributionBar } from "./StatusDistributionBar";
import { StatusStageCard } from "./StatusStageCard";
import { StageCreatorsPopover } from "./StageCreatorsPopover";

const Section = styled.section<{ $empty?: boolean }>`
	width: 100%;
	background: ${(p) => (p.$empty ? "#ebeefd" : "rgba(255, 255, 255, 0.7)")};
	border: 1px ${(p) => (p.$empty ? "dashed #bdc6f2" : "solid #dde2fa")};
	border-radius: 16px;
	box-shadow: ${(p) => (p.$empty ? "none" : "0 4px 6px rgba(21, 89, 165, 0.04)")};
	padding: 16px 20px;
	display: flex;
	flex-direction: column;
	gap: ${(p) => (p.$empty ? "0" : "12px")};
	margin-bottom: 24px;
	overflow: hidden;

	${mediaPhone`
		padding: 12px 16px;
		margin-bottom: 20px;
	`}
`;

const HeaderRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;

	${mediaPhone`
		flex-wrap: wrap;
		gap: 0 8px;
	`}
`;

/** Phones only: the stage list starts collapsed behind this toggle. */
const StagesToggle = styled.button<{ $expanded: boolean }>`
	display: none;

	${({ $expanded }) => mediaPhone`
		display: inline-flex;
		order: 1;
		align-items: center;
		justify-content: center;
		width: ${TAP_TARGET}px;
		height: ${TAP_TARGET}px;
		margin: -8px -12px -8px auto;
		padding: 0;
		border: none;
		border-radius: 50%;
		background: transparent;
		color: #3a0ca3;
		font-size: 12px;
		cursor: pointer;

		.anticon {
			transition: transform 0.2s ease;
			transform: rotate(${$expanded ? 180 : 0}deg);
		}
	`}

	&:focus-visible {
		outline: 2px solid #3a0ca3;
		outline-offset: -2px;
	}

	${pressedTint}
`;

const Title = styled.h2`
	margin: 0;
	font-family: "Poppins", sans-serif;
	font-size: 14px;
	font-weight: 600;
	color: #303030;
	white-space: nowrap;
`;

const MetaRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	font-family: "Poppins", sans-serif;
	font-size: 13px;
	font-weight: 400;
	white-space: nowrap;

	${mediaPhone`
		order: 2;
		flex: 1 1 100%;
		min-width: 0;
		font-size: 12px;
		overflow: hidden;

		& > :last-child {
			min-width: 0;
			overflow: hidden;
			text-overflow: ellipsis;
		}
	`}
`;

/* Phones say "Updated"; wider screens keep "Last updated". */
const FullPrefix = styled.span`
	${mediaPhone`
		display: none;
	`}
`;

const ShortPrefix = styled.span`
	display: none;

	${mediaPhone`
		display: inline;
	`}
`;

const MetaText = styled.span`
	color: #707070;
`;

const MetaSep = styled.span`
	color: #c4c4c4;
`;

const CardsGrid = styled.div<{ $single?: boolean; $collapsed: boolean }>`
	display: grid;
	grid-template-columns: ${(p) => (p.$single ? "1fr" : "repeat(4, minmax(0, 1fr))")};
	gap: 18px 8px;
	width: 100%;

	${(p) =>
		!p.$single
			? `
	@media (max-width: 1100px) {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	`
			: ""}

	/* Phones: flat divider rows, hidden until the toggle opens them. */
	${({ $collapsed }) => mediaPhone`
		display: ${$collapsed ? "none" : "grid"};
		grid-template-columns: 1fr;
		gap: 0;
		margin-top: -4px;

		& > :not(:last-child) button {
			border-bottom: 1px solid #e6e9f8;
		}
	`}
`;

const EmptyPlaceholder = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 8px;
	width: 100%;
	min-height: 93px;
	padding: 28px 12px;
	background: #ebeefd;
	border-radius: 10px;
	text-align: center;
`;

const EmptyIcon = styled(Users)`
	width: 24px;
	height: 24px;
	color: #c4c4c4;
	stroke-width: 1.75;
`;

const EmptyTitle = styled.p`
	margin: 0;
	font-family: "Poppins", sans-serif;
	font-size: 14px;
	font-weight: 500;
	color: #454545;
`;

const EmptySubtitle = styled.p`
	margin: 0;
	font-family: "Poppins", sans-serif;
	font-size: 12px;
	font-weight: 400;
	color: #9ca3af;
`;

const InlineEmpty = styled.div`
	padding: 12px 0 4px;
	font-family: "Poppins", sans-serif;
	font-size: 14px;
	color: #767676;
`;

interface CampaignStatusTrackingSectionProps {
	campaignId: string;
	campaignName?: string | null;
	viewContext: StatusTrackingViewContext;
	userRole: string;
	onDataChange?: () => void;
}

export const CampaignStatusTrackingSection: React.FC<CampaignStatusTrackingSectionProps> = ({
	campaignId,
	viewContext,
	userRole,
}) => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const sectionRef = useRef<HTMLElement | null>(null);
	const hasTrackedViewRef = useRef(false);
	const hasTrackedEmptyRef = useRef(false);
	const hasTrackedMismatchRef = useRef(false);
	const trackedInvalidRef = useRef<Set<string>>(new Set());
	const [openStage, setOpenStage] = useState<CampaignPipelineStage | null>(null);
	const [stagesExpanded, setStagesExpanded] = useState(false);
	const stagesId = `campaign-progress-stages-${campaignId}`;

	const { data, loading, error } = useCampaignStatusTracking(campaignId);
	const analytics = useCampaignStatusTrackingAnalytics({
		campaignId,
		viewContext,
		userRole,
		totalRoster: data?.totalRoster ?? 0,
	});

	const displayData = data;
	const segments = useMemo(
		() => (displayData ? computeDistributionSegments(displayData.stageCounts, displayData.totalRoster) : []),
		[displayData],
	);
	const visibleCards = useMemo(() => (displayData ? getVisibleStageCards(displayData) : []), [displayData]);

	const isEmptyRoster = (displayData?.totalRoster ?? 0) === 0 && (displayData?.droppedCount ?? 0) === 0;
	const isEmptyPipeline = (displayData?.totalRoster ?? 0) === 0 && (displayData?.droppedCount ?? 0) > 0;

	useEffect(() => {
		if (!displayData || hasTrackedViewRef.current) return;
		const node = sectionRef.current;
		if (!node) return;

		const observer = new IntersectionObserver(
			(entries) => {
				const visible = entries.some((entry) => entry.isIntersecting);
				if (!visible || hasTrackedViewRef.current) return;
				hasTrackedViewRef.current = true;
				analytics.trackOverviewViewed(displayData);
			},
			{ threshold: 0.2 },
		);

		observer.observe(node);
		return () => observer.disconnect();
	}, [analytics, displayData]);

	useEffect(() => {
		if (!displayData) return;
		if (isEmptyRoster && !hasTrackedEmptyRef.current) {
			hasTrackedEmptyRef.current = true;
			analytics.trackEmptyStateShown(displayData);
		}
	}, [analytics, displayData, isEmptyRoster]);

	useEffect(() => {
		if (!displayData) return;
		for (const invalid of displayData.invalidCreators) {
			if (trackedInvalidRef.current.has(invalid.assignmentId)) continue;
			trackedInvalidRef.current.add(invalid.assignmentId);
			analytics.trackInvalidStageDetected(invalid.creatorId, invalid.rawStageValue, displayData);
		}
	}, [analytics, displayData]);

	useEffect(() => {
		if (!displayData?.reconciliation.mismatch || hasTrackedMismatchRef.current) return;
		hasTrackedMismatchRef.current = true;
		analytics.trackReconciliationMismatch(displayData);
	}, [analytics, displayData]);

	useEffect(() => {
		return () => {
			if (displayData) {
				analytics.trackOverviewDwellTime(displayData);
			}
		};
	}, [analytics, displayData]);

	const handleCreatorClick = useCallback(
		(stage: CampaignPipelineStage, creator: CampaignStatusTrackingCreator) => {
			if (!displayData) return;

			analytics.trackCreatorHandleClicked(stage, creator.creatorId, "Content Execution", displayData);
			setOpenStage(null);

			const targetBlockId = creator.creatorBlockId || creator.deepLinkBlockId;
			if (!targetBlockId) return;

			const params = new URLSearchParams(searchParams);
			params.set("campaignId", campaignId);
			params.set("blockId", targetBlockId);
			navigate(`/campaign-detail?${params.toString()}`);
		},
		[analytics, campaignId, displayData, navigate, searchParams],
	);

	const handleStageHoverOpen = useCallback(
		(stage: CampaignPipelineStage, count: number, cardRank: number) => {
			if (!displayData) return;
			analytics.trackStageCardClicked(stage, count, cardRank, displayData);
			setOpenStage(stage);
		},
		[analytics, displayData],
	);

	if (loading && !displayData) {
		return (
			<Section ref={sectionRef} aria-busy="true">
				<Title>Campaign Progress</Title>
				<InlineEmpty>Loading campaign progress…</InlineEmpty>
			</Section>
		);
	}

	if (error && !displayData) {
		return (
			<Section ref={sectionRef}>
				<Title>Campaign Progress</Title>
				<InlineEmpty>{error}</InlineEmpty>
			</Section>
		);
	}

	if (!displayData) return null;

	const activeCreators = displayData.totalRoster;

	if (isEmptyRoster) {
		return (
			<Section ref={sectionRef} $empty aria-label="Campaign progress tracking">
				<HeaderRow>
					<Title>Campaign Progress</Title>
					<MetaText>0 creators</MetaText>
				</HeaderRow>
				<EmptyPlaceholder>
					<EmptyIcon aria-hidden />
					<EmptyTitle>No creators added yet</EmptyTitle>
					<EmptySubtitle>
						Status breakdown will appear once creators are added to Content Execution
					</EmptySubtitle>
				</EmptyPlaceholder>
			</Section>
		);
	}

	const rosterLabel = activeCreators === 1 ? "1 active creator" : `${activeCreators} active creators`;

	return (
		<Section ref={sectionRef} aria-label="Campaign progress tracking">
			<HeaderRow>
				<Title>Campaign Progress</Title>
				<MetaRow>
					<MetaText>{rosterLabel}</MetaText>
					<MetaSep>·</MetaSep>
					<MetaText>
						<FullPrefix>Last updated </FullPrefix>
						<ShortPrefix>Updated </ShortPrefix>
						{formatUpdatedAtValue(displayData.lastUpdatedAt)}
					</MetaText>
				</MetaRow>
				{visibleCards.length > 0 && (
					<StagesToggle
						type="button"
						$expanded={stagesExpanded}
						aria-expanded={stagesExpanded}
						aria-controls={stagesId}
						aria-label={stagesExpanded ? "Hide stage breakdown" : "Show stage breakdown"}
						onClick={() => setStagesExpanded((value) => !value)}
					>
						<DownOutlined />
					</StagesToggle>
				)}
			</HeaderRow>

			{displayData.reconciliation.mismatch && (
				<Alert
					type="warning"
					showIcon
					message="Data integrity warning"
					description="Stage counts do not reconcile with the active roster total. Counts shown are from the last good snapshot."
				/>
			)}

			{displayData.invalidCreators.length > 0 && (
				<Alert
					type="warning"
					showIcon
					message={`${displayData.invalidCreators.length} creator(s) have an unresolved stage`}
					description={displayData.invalidCreators.map((c) => `@${c.handle.replace(/^@/, "")}`).join(", ")}
				/>
			)}

			{!isEmptyPipeline && segments.length > 0 && <StatusDistributionBar segments={segments} />}

			{isEmptyPipeline && (
				<InlineEmpty>No creators in the active pipeline. All roster members have been dropped.</InlineEmpty>
			)}

			{visibleCards.length > 0 && (
				<CardsGrid id={stagesId} $single={visibleCards.length === 1} $collapsed={!stagesExpanded}>
					{visibleCards.map((stageDef, index) => {
						const count = displayData.stageCounts[stageDef.stage];
						const creators = displayData.creatorsByStage[stageDef.stage] ?? [];

						return (
							<StageCreatorsPopover
								key={stageDef.stage}
								stage={stageDef.stage}
								stageLabel={stageDef.label}
								creators={creators}
								open={openStage === stageDef.stage}
								onOpenChange={(open) => {
									if (open) {
										handleStageHoverOpen(stageDef.stage, count, index + 1);
									} else if (openStage === stageDef.stage) {
										setOpenStage(null);
									}
								}}
								onCreatorClick={(creator) => handleCreatorClick(stageDef.stage, creator)}
							>
								<div style={{ width: "100%" }}>
									<StatusStageCard
										stage={stageDef}
										count={count}
										active={openStage === stageDef.stage}
									/>
								</div>
							</StageCreatorsPopover>
						);
					})}
				</CardsGrid>
			)}
		</Section>
	);
};

export type { CampaignStatusTrackingData };
